<template>
  <div>
    <v-container class="toolbar__container">
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete
            v-model="idEquipamento"
            :items="equipamentosComInventario"
            :item-text="rotuloEquipamento"
            item-value="id_equipamento"
            clearable
            :label="$t('statusDispositivos.filters.equipment')"
            :placeholder="$t('statusDispositivos.messages.allEquipments')"
            persistent-placeholder
          />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select v-model="status" :items="statuses" clearable :label="$t('statusDispositivos.filters.errorStatus')" />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select v-model="operacao" :items="operacoes" clearable :label="$t('statusDispositivos.filters.operation')" />
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-select v-model="causa" :items="causas" clearable :label="$t('statusDispositivos.filters.cause')" />
        </v-col>
        <v-col cols="12" sm="6" md="2" class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn small fab color="primary darken-1" class="white--text" v-bind="attrs" v-on="on" :loading="loading" @click="popula">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('statusDispositivos.actions.search') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <merus-data-table
      :key="tableKey"
      cod-tabela="EQUIPAMENTOS__VALIDADORES_ERROS_SYNC"
      v-model="selecionados"
      :headers="headers"
      :items="itens"
      :loading="loading"
      item-key="id_erro"
      :show-select="podeReaplicar"
      @update:headers="headers = $event"
    >
      <template v-slot:toolbar>
        <CadastroToolbar
          :title="$t('statusDispositivos.modules.syncErrors')"
          :token="token"
          :items="itens"
          csv-title="dispositivos_errossync"
          :csv-headers="headers"
          :pdf-title="$t('statusDispositivos.modules.syncErrors')"
          :pdf-headers="headers"
          pdf-filename="dispositivos_errossync"
          hide-search
        >
          <template v-if="podeReaplicar" v-slot:add>
            <v-tooltip top max-width="360">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn
                    fab
                    small
                    color="primary"
                    dark
                    class="mb-2 mr-2"
                    :disabled="selecionados.length === 0 || reprocessando"
                    :loading="reprocessando"
                    @click="reaplica(selecionados)"
                  >
                    <v-icon>mdi-sync</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>{{ $t('statusDispositivos.actions.reapplySelected') }} — {{ $t('equipment.help.isapiReapply') }}</span>
            </v-tooltip>
          </template>
        </CadastroToolbar>
      </template>

      <template v-slot:item.status_extenso="{ item }">
        <v-chip small dark :color="corStatus(item.status)">{{ item.status_extenso }}</v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-tooltip v-if="podeReaplicar" top>
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <v-btn icon small color="primary" :disabled="item.status === 'resolved' || reprocessando" @click="reaplica([item])">
                <v-icon small>mdi-sync</v-icon>
              </v-btn>
            </span>
          </template>
          <span>{{ $t('statusDispositivos.actions.reapply') }}</span>
        </v-tooltip>
      </template>
    </merus-data-table>
  </div>
</template>

<script>
import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';
import MerusDataTable from '../../Tables/MerusDataTable.vue';
import CadastroToolbar from '../../Toolbars/CadastroToolbar.vue';
import dispositivosFormatters from './dispositivosFormatters';
import recorteEquipamentos from './recorteEquipamentos';

const STATUSES = ['pending', 'exhausted', 'resolved'];
const OPERACOES = ['ADD_PERSON', 'DEL_PERSON', 'ADD_CARD', 'DEL_CARD'];
// Causa gravada pelo sync: separa oscilação de rede de dado recusado pelo aparelho.
const CAUSAS = ['comunicacao', 'dado'];

export default {
  name: 'ErrosSync',
  components: { MerusDataTable, CadastroToolbar },
  mixins: [dispositivosFormatters, recorteEquipamentos],
  props: {
    equipamentos: { type: Array, default: () => [] },
    token: { type: String, default: null },
    // Reabrir erros (isapi/reaplicaisapierro) segue quem pode editar equipamento.
    podeReaplicar: { type: Boolean, default: false },
  },
  data: () => ({
    idEquipamento: null,
    status: null,
    operacao: null,
    causa: null,
    loading: false,
    reprocessando: false,
    itens: [],
    selecionados: [],
    headers: [],
  }),
  computed: {
    // A fila de erros é pessoa a pessoa no aparelho (ISAPI): o HikCentral, que inventaria só
    // o nível de acesso, nunca teria linha nenhuma e só poluiria o filtro.
    equipamentosComInventario() {
      return (this.equipamentos || []).filter((e) => e.fonte_inventario === 'APARELHO');
    },
    tableKey() {
      return `dispositivos-erros-${this.$i18n.locale}`;
    },
    statuses() {
      return STATUSES.map((v) => ({ value: v, text: this.$t(`statusDispositivos.errorStatus.${v}`) }));
    },
    operacoes() {
      return OPERACOES.map((v) => ({ value: v, text: this.$t(`statusDispositivos.operation.${v}`) }));
    },
    causas() {
      return CAUSAS.map((v) => ({ value: v, text: this.$t(`statusDispositivos.cause.${v}`) }));
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initializeHeaders();
      this.itens = this.itens.map((i) => this.decora(i));
    },
  },
  created() {
    this.initializeHeaders();
    this.popula();
  },
  methods: {
    rotuloEquipamento(item) {
      return item.cod_veiculo ? `${item.nome_equipamento} (${item.cod_veiculo})` : item.nome_equipamento;
    },
    initializeHeaders() {
      const h = this.$t.bind(this);
      this.headers = [
        { text: h('statusDispositivos.headers.equipment'), value: 'nome_equipamento', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.vehicle'), value: 'cod_veiculo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.personName'), value: 'nome_completo', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.cardNo'), value: 'card_no', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.operation'), value: 'operacao_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.message'), value: 'mensagem', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.cause'), value: 'causa_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.attempts'), value: 'tentativas', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.errorStatus'), value: 'status_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.firstErrorAt'), value: 'dt_primeiro_erro_extenso', sortable: true, filtrable: true },
        { text: h('statusDispositivos.headers.lastAttemptAt'), value: 'dt_ultima_tentativa_extenso', sortable: true, filtrable: true },
      ];
    },
    corStatus(status) {
      if (status === 'resolved') return 'green';
      if (status === 'exhausted') return 'red';
      return 'orange darken-2';
    },
    decora(linha) {
      return {
        ...linha,
        operacao_extenso: this.traduzDominio('operation', linha.operacao),
        status_extenso: this.traduzDominio('errorStatus', linha.status),
        causa_extenso: this.fmtTexto(linha.causa ? this.traduzDominio('cause', linha.causa) : null),
        dt_primeiro_erro_extenso: this.fmtDataHora(linha.dt_primeiro_erro),
        dt_ultima_tentativa_extenso: this.fmtDataHora(linha.dt_ultima_tentativa),
      };
    },
    popula() {
      this.loading = true;
      this.selecionados = [];
      const filtros = {};
      if (this.idEquipamento != null) filtros.id_equipamento = this.idEquipamento;
      if (this.status) filtros.status = this.status;
      if (this.operacao) filtros.operacao = this.operacao;
      if (this.causa) filtros.causa = this.causa;

      apiController.request(apiController.endpoints.isapiGetIsapierrossync, {
        data: filtros,
        onSuccess: (response) => {
          this.itens = this.recortaDaLista(response.data || []).map((linha) => this.decora(linha));
        },
        onError: () => {
          this.itens = [];
        },
      })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    // Reabrir só zera as tentativas; quem reenvia de fato é o próximo sync (rotina ou disparo manual).
    reaplica(linhas) {
      const ids = (linhas || []).filter((l) => l.status !== 'resolved').map((l) => l.id_erro);
      if (ids.length === 0) {
        EventBus.$emit('show-error', { error: this.$t('statusDispositivos.messages.selectErrors') });
        return;
      }
      this.reprocessando = true;
      apiController.request(apiController.endpoints.isapiReaplicaisapierro, {
        data: { ids_erro: ids },
        onSuccess: (response) => {
          const dados = response.data || {};
          EventBus.$emit('show-success', {
            message: this.$t('statusDispositivos.messages.reapplied', { count: dados.reabertos != null ? dados.reabertos : ids.length }),
          });
          this.popula();
          this.$emit('refresh');
        },
      })
        .catch(() => {})
        .finally(() => {
          this.reprocessando = false;
        });
    },
  },
};
</script>
